<template>
  <section v-if="loading || needsAssignment" class="ra-panel" :class="{ 'ra-panel--none': !loading && !hasFree }">
    <!-- Cabecera -->
    <div class="ra-head">
      <span class="ra-head__icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
          <path d="M2 4v16"/><path d="M2 9h16a4 4 0 0 1 4 4v7"/><path d="M2 16h20"/><path d="M6 9V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3"/>
        </svg>
      </span>
      <div class="ra-head__text">
        <h3 class="ra-title">{{ $t('reservasPanels.roomAssign.title') }}</h3>
        <p class="ra-sub">
          <template v-if="unitName">{{ $t('reservasPanels.roomAssign.subWithUnitPre') }} <strong>{{ unitName }}</strong> {{ $t('reservasPanels.roomAssign.subWithUnitPost') }}</template>
          <template v-else>{{ $t('reservasPanels.roomAssign.subNoUnit') }}</template>
        </p>
      </div>
    </div>

    <!-- Cargando -->
    <div v-if="loading" class="ra-loading">
      <span class="ra-spinner"></span> {{ $t('reservasPanels.roomAssign.loading') }}
    </div>

    <template v-else>
      <!-- HAY habitaciones libres → asignar directa -->
      <div v-if="hasFree" class="ra-body">
        <p class="ra-hint">
          {{ $t('reservasPanels.roomAssign.freePre') }} <strong>{{ dateLabel }}</strong>. {{ $t('reservasPanels.roomAssign.freePost') }}
        </p>

        <div class="ra-rooms">
          <button
            v-for="room in freeRooms"
            :key="room.id"
            type="button"
            class="ra-room"
            :class="{ 'is-selected': selectedId === room.id }"
            @click="selectedId = room.id"
          >
            <span class="ra-room__radio"></span>
            <span class="ra-room__label">{{ room.label }}</span>
            <span class="ra-room__badge">{{ $t('reservasPanels.roomAssign.freeBadge') }}</span>
          </button>
        </div>

        <div class="ra-actions">
          <button
            type="button"
            class="ra-assign"
            :disabled="!selectedId || assigning"
            @click="assign"
          >
            <span v-if="assigning" class="ra-spinner ra-spinner--light"></span>
            {{ assigning ? $t('reservasPanels.roomAssign.assigning') : $t('reservasPanels.roomAssign.title') }}
          </button>
          <span v-if="occupiedRooms.length" class="ra-occ-note">
            {{ $tc('reservasPanels.roomAssign.occupiedNote', occupiedRooms.length, { count: occupiedRooms.length }) }}
          </span>
        </div>
      </div>

      <!-- NO hay libres → hay que reorganizar -->
      <div v-else class="ra-body">
        <div class="ra-warn">
          <span class="ra-warn__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><circle cx="12" cy="17" r="1" fill="currentColor" stroke="none"/>
            </svg>
          </span>
          <div>
            <strong>{{ $t('reservasPanels.roomAssign.noneFreeTitle') }}</strong>
            <p>
              {{ $t('reservasPanels.roomAssign.noFreePre') }} <strong>{{ unitName || $t('reservasPanels.roomAssign.thisType') }}</strong> {{ $t('reservasPanels.roomAssign.noFreeMid', { date: dateLabel }) }} <strong>{{ $t('reservasPanels.roomAssign.reorganizeBold') }}</strong> {{ $t('reservasPanels.roomAssign.noFreeMid2') }} <em>{{ $t('reservasPanels.roomAssign.organizeBtn') }}</em>{{ $t('reservasPanels.roomAssign.noFreeTail') }}
            </p>
          </div>
        </div>

        <ul class="ra-occlist">
          <li v-for="room in rooms" :key="room.id" class="ra-occrow">
            <span class="ra-occrow__label">{{ room.label }}</span>
            <span v-if="room.is_maintenance" class="ra-tag ra-tag--maint">{{ $t('reservasPanels.roomAssign.tagMaintenance') }}</span>
            <span v-else-if="room.blocked" class="ra-tag ra-tag--blocked">{{ $t('reservasPanels.roomAssign.tagBlocked') }}</span>
            <span v-else class="ra-tag ra-tag--occ">{{ occupiedText(room) }}</span>
          </li>
        </ul>
      </div>
    </template>
  </section>
</template>

<script>
import axios from "axios";

export default {
  name: "RoomAssignmentPanel",
  props: {
    reservaId: { type: [String, Number], required: true },
    userId: { type: [String, Number], default: null },
  },
  data() {
    return {
      loading: true,
      needsAssignment: false,
      unitName: "",
      dates: null,
      rooms: [],
      hasFree: false,
      selectedId: null,
      assigning: false,
    };
  },
  computed: {
    freeRooms() {
      return this.rooms.filter((r) => r.free);
    },
    occupiedRooms() {
      return this.rooms.filter((r) => !r.free);
    },
    dateLabel() {
      if (!this.dates) return this.$t("reservasPanels.roomAssign.theseDates");
      return `${this.fmt(this.dates.from)} → ${this.fmt(this.dates.to)}`;
    },
  },
  mounted() {
    this.fetchRooms();
  },
  methods: {
    fmt(d) {
      if (!d) return "—";
      try {
        return new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "short" });
      } catch {
        return d;
      }
    },
    occupiedText(room) {
      const who = (room.occupied_by || []).map((o) => o.name).filter(Boolean);
      if (!who.length) return this.$t("reservasPanels.roomAssign.occupied");
      return who.length === 1 ? who[0] : `${who[0]} +${who.length - 1}`;
    },
    async fetchRooms() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/reservations/${this.reservaId}/assignable-rooms`, {
          params: { user_id: this.userId },
        });
        this.needsAssignment = !!data.needs_assignment;
        this.unitName = data.accommodation_unit?.name || "";
        this.dates = data.dates || null;
        this.rooms = Array.isArray(data.rooms) ? data.rooms : [];
        this.hasFree = !!data.has_free;
        // Preselecciona la primera libre para que asignar sea un solo clic.
        const firstFree = this.rooms.find((r) => r.free);
        this.selectedId = firstFree ? firstFree.id : null;
      } catch (e) {
        console.error("[RoomAssignmentPanel] fetch error", e);
        this.needsAssignment = false;
      } finally {
        this.loading = false;
      }
    },
    async assign() {
      if (!this.selectedId || this.assigning) return;
      this.assigning = true;
      try {
        await axios.post("/change-room-booking", {
          reserva_id: this.reservaId,
          new_room_id: this.selectedId,
          current_room_id: null,
          swap: false,
        });
        const room = this.rooms.find((r) => r.id === this.selectedId);
        this.notify("success", this.$t("reservasPanels.roomAssign.assignedOk", { label: room ? room.label : "" }));
        // El detalle se recarga (needs_room_assignment pasa a false → el panel se oculta).
        this.$emit("assigned", { roomId: this.selectedId });
      } catch (e) {
        const msg = e?.response?.data?.message || this.$t("reservasPanels.roomAssign.assignError");
        this.notify("error", msg);
        // Si la habitación se ocupó entretanto, refresca la disponibilidad.
        if (e?.response?.status === 409 || e?.response?.status === 422) this.fetchRooms();
      } finally {
        this.assigning = false;
      }
    },
    notify(type, message) {
      const t = this.$toast;
      if (t?.[type]) return t[type](message);
      if (t?.add) {
        return t.add({
          severity: type === "error" ? "error" : "success",
          summary: type === "error" ? this.$t("reservasPanels.roomAssign.notifyError") : this.$t("reservasPanels.roomAssign.notifyDone"),
          detail: message,
          life: 3500,
        });
      }
      /* eslint-disable-next-line no-console */
      console.log(`[${type}] ${message}`);
    },
  },
};
</script>

<style scoped>
.ra-panel {
  background: #f5f3fe;
  border: 1px solid #e6e0fb;
  border-radius: 16px;
  padding: 18px 20px;
  font-family: "DM Sans", sans-serif;
  text-align: left;
}
.ra-panel--none {
  background: #fff8f1;
  border-color: #f6e2cf;
}

.ra-head {
  display: flex;
  align-items: flex-start;
  gap: 13px;
}
.ra-head__icon {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 11px;
  background: #ece7fb;
  color: #673de6;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ra-panel--none .ra-head__icon {
  background: #fdecd2;
  color: #c2820b;
}
.ra-head__icon svg { width: 19px; height: 19px; }
.ra-title {
  margin: 0 0 2px;
  font-size: 15px;
  font-weight: 700;
  color: #1c1630;
  letter-spacing: -0.01em;
}
.ra-sub {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #5c5470;
}
.ra-sub strong { color: #3f356a; font-weight: 700; }

.ra-loading {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 14px;
  font-size: 13px;
  color: #6b6480;
}

.ra-body { margin-top: 15px; }
.ra-hint {
  margin: 0 0 11px;
  font-size: 12.5px;
  color: #5c5470;
}
.ra-hint strong { color: #3f356a; }

.ra-rooms {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
}
.ra-room {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  border: 1.5px solid #e2dbf6;
  background: #fff;
  border-radius: 11px;
  padding: 9px 13px;
  cursor: pointer;
  transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
}
.ra-room:hover { border-color: #c9bcf3; }
.ra-room.is-selected {
  border-color: #673de6;
  background: #f3f0fe;
  box-shadow: 0 0 0 3px rgba(103, 61, 230, 0.12);
}
.ra-room__radio {
  width: 15px;
  height: 15px;
  border-radius: 999px;
  border: 2px solid #cabff0;
  position: relative;
  flex-shrink: 0;
  transition: border-color .15s ease;
}
.ra-room.is-selected .ra-room__radio {
  border-color: #673de6;
}
.ra-room.is-selected .ra-room__radio::after {
  content: "";
  position: absolute;
  inset: 2px;
  border-radius: 999px;
  background: #673de6;
}
.ra-room__label {
  font-size: 13.5px;
  font-weight: 700;
  color: #221a3a;
}
.ra-room__badge {
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .03em;
  color: #0f8a4f;
  background: #dff5e8;
  border-radius: 999px;
  padding: 2px 7px;
}

.ra-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 16px;
  flex-wrap: wrap;
}
.ra-assign {
  border: none;
  background: #673de6;
  color: #fff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  border-radius: 10px;
  min-height: 40px;
  padding: 0 20px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 6px 18px rgba(103, 61, 230, 0.28);
  transition: background .15s ease, transform .12s ease, opacity .15s ease;
}
.ra-assign:hover:not(:disabled) { background: #5a32d0; transform: translateY(-1px); }
.ra-assign:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
.ra-occ-note { font-size: 12px; color: #8b849c; }

/* Sin disponibilidad */
.ra-warn {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.ra-warn__icon {
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 9px;
  background: #fdecd2;
  color: #c2820b;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ra-warn__icon svg { width: 17px; height: 17px; }
.ra-warn strong { color: #7c4a03; font-size: 13.5px; }
.ra-warn p {
  margin: 5px 0 0;
  font-size: 12.5px;
  line-height: 1.55;
  color: #7a6a52;
}
.ra-warn em { font-style: normal; font-weight: 700; color: #b45309; }

.ra-occlist {
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ra-occrow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: #fff;
  border: 1px solid #f0e6d6;
  border-radius: 9px;
  padding: 8px 12px;
}
.ra-occrow__label { font-size: 13px; font-weight: 700; color: #3a3348; }
.ra-tag {
  font-size: 10.5px;
  font-weight: 700;
  border-radius: 999px;
  padding: 3px 9px;
  white-space: nowrap;
}
.ra-tag--occ { background: #eef1f6; color: #55607a; }
.ra-tag--blocked { background: #fdecec; color: #c0392b; }
.ra-tag--maint { background: #e8eaf9; color: #4e61cb; }

.ra-spinner {
  width: 15px;
  height: 15px;
  border: 2px solid #d6ccf6;
  border-top-color: #673de6;
  border-radius: 999px;
  animation: ra-spin .7s linear infinite;
  display: inline-block;
}
.ra-spinner--light {
  border-color: rgba(255, 255, 255, .4);
  border-top-color: #fff;
}
@keyframes ra-spin { to { transform: rotate(360deg); } }

@media (max-width: 600px) {
  .ra-panel { padding: 15px 15px; }
  .ra-room { flex: 1 1 auto; justify-content: center; }
}
</style>
